import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { analyticsService } from './analytics-service';

/**
 * Hook React pour utiliser Firebase Analytics
 * 
 * @example
 * ```tsx
 * const { logEvent, logPageView } = useAnalytics();
 * 
 * // Enregistrer un événement personnalisé
 * logEvent('button_click', { button_name: 'submit' });
 * 
 * // Enregistrer une vue de page
 * logPageView('Page d\'accueil');
 * ```
 */
export const useAnalytics = () => {
  const location = useLocation();

  // Initialiser les propriétés utilisateur au premier chargement
  useEffect(() => {
    analyticsService.initializeUserProperties();
  }, []);

  // Enregistre automatiquement les changements de page
  useEffect(() => {
    // Petit délai pour laisser le temps à Helmet de mettre à jour le titre
    const timer = setTimeout(() => {
      analyticsService.logPageView(
        document.title || location.pathname,
        location.pathname
      );
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return {
    logEvent: analyticsService.logEvent.bind(analyticsService),
    logPageView: analyticsService.logPageView.bind(analyticsService),
    logPurchase: analyticsService.logPurchase.bind(analyticsService),
    logAddToCart: analyticsService.logAddToCart.bind(analyticsService),
    logBeginCheckout: analyticsService.logBeginCheckout.bind(analyticsService),
    logConversion: analyticsService.logConversion.bind(analyticsService),
    setUserId: analyticsService.setUserId.bind(analyticsService),
    setUserProperties: analyticsService.setUserProperties.bind(analyticsService),
  };
};

/**
 * Hook pour enregistrer automatiquement les vues de page
 * Utilisez ce hook dans votre composant App ou Router
 */
export const usePageTracking = () => {
  const location = useLocation();

  // Initialiser les propriétés utilisateur au premier chargement
  useEffect(() => {
    analyticsService.initializeUserProperties();
  }, []);

  useEffect(() => {
    // Petit délai pour laisser le temps à Helmet de mettre à jour le titre
    const timer = setTimeout(() => {
      analyticsService.logPageView(
        document.title || location.pathname,
        location.pathname
      );
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname]);
};
